import { useState, useEffect, useCallback } from 'react';
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@easyflow/ui-master/components/ui/tabs';
import {
  FlaskConical, Play, CheckCircle2, XCircle, Clock, RefreshCw,
  AlertTriangle, Lightbulb, History, Loader2, ChevronDown, ChevronRight
} from 'lucide-react';
import apiClient from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

interface TestResult {
  name: string;
  suite: string;
  status: 'pass' | 'fail';
  duration: number;
  error?: string;
  suggestion?: string;
}

interface TestRun {
  id: string;
  timestamp: string;
  category: string;
  tenantFilter: string;
  totalTests: number;
  passed: number;
  failed: number;
  duration: number;
  results: TestResult[];
}

interface TestCategory {
  key: string;
  label: string;
  testFiles: string[];
}

interface TenantInfo {
  id: string;
  name: string;
  company_name?: string;
  slug: string;
}

interface TenantsResponse {
  tenants: TenantInfo[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

const FALLBACK_CATEGORIES: TestCategory[] = [
  { key: 'all', label: 'Tutti i Test', testFiles: [] },
  { key: 'llm-factory', label: 'LLM Factory', testFiles: ['llm-factory'] },
  { key: 'rag-pipeline', label: 'RAG Pipeline', testFiles: ['rag-pipeline-e2e', 'rag-helpers'] },
  { key: 'tenant-isolation', label: 'Tenant Isolation', testFiles: ['tenant-isolation'] },
  { key: 'mcp-permissions', label: 'MCP Permissions', testFiles: ['mcp-permissions'] },
  { key: 'encryption', label: 'Encryption', testFiles: ['ai-encryption'] },
  { key: 'provider-fallback', label: 'Provider Fallback', testFiles: ['provider-fallback'] },
];

function formatDuration(ms: number): string {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${Math.floor(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
}

function formatTimestamp(ts: string): string {
  const d = new Date(ts);
  return d.toLocaleString('it-IT', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

function ResultCard({ result }: { result: TestResult }) {
  const [expanded, setExpanded] = useState(false);
  const isPassed = result.status === 'pass';

  return (
    <div
      className={`rounded-xl border p-4 transition-all ${
        isPassed
          ? 'border-emerald-200 dark:border-emerald-500/30 bg-emerald-50/50 dark:bg-emerald-500/5'
          : 'border-red-200 dark:border-red-500/30 bg-red-50/50 dark:bg-red-500/5'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {isPassed ? (
            <CheckCircle2 className="h-5 w-5 text-emerald-500 shrink-0 mt-0.5" />
          ) : (
            <XCircle className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          )}
          <div className="min-w-0 flex-1">
            <p className={`text-sm font-medium truncate ${
              isPassed ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'
            }`}>
              {result.name}
            </p>
            <p className="text-xs text-muted-foreground dark:text-muted-foreground/70 mt-0.5">
              Suite: {result.suite}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-muted-foreground dark:text-muted-foreground/70 flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDuration(result.duration)}
          </span>
          {(result.error || result.suggestion) && (
            <button
              onClick={() => setExpanded(!expanded)}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-white/10"
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          )}
        </div>
      </div>

      {expanded && (result.error || result.suggestion) && (
        <div className="mt-3 space-y-2 pl-8">
          {result.error && (
            <div className="rounded-lg bg-red-100 dark:bg-red-500/10 p-3 border border-red-200 dark:border-red-500/20">
              <p className="text-xs font-medium text-red-700 dark:text-red-400 mb-1 flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" /> Errore
              </p>
              <pre className="text-xs text-red-600 dark:text-red-300 whitespace-pre-wrap break-words max-h-40 overflow-y-auto">
                {result.error}
              </pre>
            </div>
          )}
          {result.suggestion && (
            <div className="rounded-lg bg-amber-100 dark:bg-amber-500/10 p-3 border border-amber-200 dark:border-amber-500/20">
              <p className="text-xs font-medium text-amber-700 dark:text-amber-400 mb-1 flex items-center gap-1">
                <Lightbulb className="h-3 w-3" /> Suggerimento
              </p>
              <p className="text-xs text-amber-600 dark:text-amber-300">{result.suggestion}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function HistoryRow({ run, categories }: { run: TestRun; categories: TestCategory[] }) {
  const allPassed = run.failed === 0;

  return (
    <tr className="border-b border-border dark:border-white/5 hover:bg-muted/50 dark:hover:bg-white/5 transition-colors">
      <td className="px-4 py-3 text-sm text-foreground dark:text-muted-foreground/60">
        {formatTimestamp(run.timestamp)}
      </td>
      <td className="px-4 py-3">
        <Badge variant="outline" className="text-xs">
          {categories.find(c => c.key === run.category)?.label || run.category}
        </Badge>
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground dark:text-muted-foreground/70">
        {run.tenantFilter || '-'}
      </td>
      <td className="px-4 py-3">
        {allPassed ? (
          <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 border-0">
            <CheckCircle2 className="h-3 w-3 mr-1" /> {run.passed} passed
          </Badge>
        ) : (
          <div className="flex items-center gap-2">
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 border-0 text-xs">
              ✓ {run.passed}
            </Badge>
            <Badge className="bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300 border-0 text-xs">
              ✗ {run.failed}
            </Badge>
          </div>
        )}
      </td>
      <td className="px-4 py-3 text-sm text-muted-foreground dark:text-muted-foreground/70">
        {formatDuration(run.duration)}
      </td>
    </tr>
  );
}

const NATIVE_SELECT_CLASS =
  'w-full rounded-lg border border-border dark:border-white/10 bg-white dark:bg-muted text-foreground dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-inset focus:ring-purple-500';

export default function TestVerifyPage() {
  const { toast } = useToast();
  const [category, setCategory] = useState('all');
  const [tenantFilter, setTenantFilter] = useState('');
  const [tenants, setTenants] = useState<{ id: string; name: string; slug: string }[]>([]);
  const [categories, setCategories] = useState<TestCategory[]>(FALLBACK_CATEGORIES);
  const [running, setRunning] = useState(false);
  const [currentRun, setCurrentRun] = useState<TestRun | null>(null);
  const [history, setHistory] = useState<TestRun[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [activeTab, setActiveTab] = useState('run');

  const loadCategories = useCallback(async () => {
    try {
      const res = await apiClient.get<TestCategory[]>('/master/test-verify/categories');
      if (res.success && res.data && res.data.length > 0) {
        setCategories(res.data);
      }
    } catch (err) {
      console.error('Failed to load categories, using fallback:', err);
    }
  }, []);

  const loadTenants = useCallback(async () => {
    try {
      const res = await apiClient.get<TenantsResponse>('/master/tenants');
      if (res.success && res.data) {
        const responseData = res.data;
        const tenantsArray = responseData.tenants || [];
        setTenants(tenantsArray.map((t: TenantInfo) => ({
          id: t.id,
          name: t.name || t.company_name || t.slug,
          slug: t.slug,
        })));
      }
    } catch (err) {
      console.error('Failed to load tenants:', err);
    }
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      setLoadingHistory(true);
      const res = await apiClient.get<TestRun[]>('/master/test-verify/history');
      if (res.success && res.data) {
        setHistory(res.data);
      }
    } catch (err) {
      console.error('Failed to load history:', err);
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  useEffect(() => {
    void loadCategories();
    void loadTenants();
    void loadHistory();
  }, [loadCategories, loadTenants, loadHistory]);

  const handleRunTests = async () => {
    if (!tenantFilter) {
      toast({
        title: 'Seleziona un tenant',
        description: 'Il filtro tenant è obbligatorio per eseguire i test.',
        variant: 'destructive',
      });
      return;
    }

    setRunning(true);
    setCurrentRun(null);

    try {
      const res = await apiClient.post<TestRun>('/master/test-verify/run', {
        category,
        tenantFilter,
      });

      if (res.success && res.data) {
        setCurrentRun(res.data);
        setHistory(prev => [res.data!, ...prev]);
        toast({
          title: res.data.failed === 0 ? 'Tutti i test superati!' : 'Alcuni test falliti',
          description: `${res.data.passed} passed, ${res.data.failed} failed in ${formatDuration(res.data.duration)}`,
          variant: res.data.failed === 0 ? 'default' : 'destructive',
        });
      } else {
        toast({
          title: 'Errore',
          description: res.error || 'Errore durante l\'esecuzione dei test',
          variant: 'destructive',
        });
      }
    } catch (err: unknown) {
      toast({
        title: 'Errore',
        description: err instanceof Error ? err.message : 'Errore di connessione',
        variant: 'destructive',
      });
    } finally {
      setRunning(false);
    }
  };

  const passedResults = currentRun?.results.filter(r => r.status === 'pass') || [];
  const failedResults = currentRun?.results.filter(r => r.status === 'fail') || [];

  const uniqueSuggestions = currentRun
    ? [...new Set(failedResults.filter(r => r.suggestion).map(r => r.suggestion!))]
    : [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-white flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shadow-lg">
              <FlaskConical className="h-5 w-5 text-white" />
            </div>
            Test & Verify
          </h1>
          <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">
            Esegui test automatizzati per verificare l'integrità del sistema backend
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="run" className="flex items-center gap-2">
            <Play className="h-4 w-4" /> Esegui Test
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center gap-2">
            <History className="h-4 w-4" /> Storico
          </TabsTrigger>
        </TabsList>

        <TabsContent value="run" className="space-y-6 mt-4">
          <GlassCard>
            <GlassCardHeader>
              <GlassCardTitle>Configurazione Test</GlassCardTitle>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-1.5">
                    Categoria Test
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={running}
                    className={NATIVE_SELECT_CLASS}
                  >
                    {categories.map(opt => (
                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground dark:text-muted-foreground/60 mb-1.5">
                    Tenant <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={tenantFilter}
                    onChange={(e) => setTenantFilter(e.target.value)}
                    disabled={running}
                    className={NATIVE_SELECT_CLASS}
                  >
                    <option value="">Seleziona tenant...</option>
                    {tenants.map(t => (
                      <option key={t.id} value={t.slug}>{t.name} ({t.slug})</option>
                    ))}
                  </select>
                </div>

                <div>
                  <Button
                    onClick={handleRunTests}
                    disabled={running || !tenantFilter}
                    className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white"
                  >
                    {running ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Esecuzione in corso...
                      </>
                    ) : (
                      <>
                        <Play className="h-4 w-4 mr-2" />
                        Avvia Test
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </GlassCardContent>
          </GlassCard>

          {running && (
            <GlassCard>
              <GlassCardContent className="py-12">
                <div className="flex flex-col items-center justify-center gap-4">
                  <div className="relative">
                    <div className="w-16 h-16 rounded-full border-4 border-purple-200 dark:border-purple-500/20"></div>
                    <div className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-purple-500 animate-spin"></div>
                  </div>
                  <div className="text-center">
                    <p className="text-lg font-medium text-foreground dark:text-white">Esecuzione test in corso...</p>
                    <p className="text-sm text-muted-foreground dark:text-muted-foreground/70 mt-1">
                      Categoria: {categories.find(c => c.key === category)?.label}
                    </p>
                  </div>
                </div>
              </GlassCardContent>
            </GlassCard>
          )}

          {currentRun && !running && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <GlassCard>
                  <GlassCardContent className="py-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-foreground dark:text-white">{currentRun.totalTests}</p>
                      <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Test Totali</p>
                    </div>
                  </GlassCardContent>
                </GlassCard>
                <GlassCard>
                  <GlassCardContent className="py-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-emerald-500">{currentRun.passed}</p>
                      <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Superati</p>
                    </div>
                  </GlassCardContent>
                </GlassCard>
                <GlassCard>
                  <GlassCardContent className="py-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-red-500">{currentRun.failed}</p>
                      <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Falliti</p>
                    </div>
                  </GlassCardContent>
                </GlassCard>
                <GlassCard>
                  <GlassCardContent className="py-4">
                    <div className="text-center">
                      <p className="text-3xl font-bold text-indigo-500">{formatDuration(currentRun.duration)}</p>
                      <p className="text-sm text-muted-foreground dark:text-muted-foreground/70">Durata</p>
                    </div>
                  </GlassCardContent>
                </GlassCard>
              </div>

              {uniqueSuggestions.length > 0 && (
                <GlassCard>
                  <GlassCardHeader>
                    <GlassCardTitle className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                      <Lightbulb className="h-5 w-5" />
                      Suggerimenti di Azione
                    </GlassCardTitle>
                  </GlassCardHeader>
                  <GlassCardContent>
                    <ul className="space-y-2">
                      {uniqueSuggestions.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-amber-700 dark:text-amber-300">
                          <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                          {s}
                        </li>
                      ))}
                    </ul>
                  </GlassCardContent>
                </GlassCard>
              )}

              <GlassCard>
                <GlassCardHeader>
                  <GlassCardTitle>Risultati Test</GlassCardTitle>
                </GlassCardHeader>
                <GlassCardContent>
                  <div className="space-y-3">
                    {failedResults.map((r, i) => (
                      <ResultCard key={`fail-${i}`} result={r} />
                    ))}
                    {passedResults.map((r, i) => (
                      <ResultCard key={`pass-${i}`} result={r} />
                    ))}
                    {currentRun.results.length === 0 && (
                      <div className="text-center py-8 text-muted-foreground dark:text-muted-foreground/70">
                        <FlaskConical className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>Nessun risultato trovato. Verifica che i file di test esistano per la categoria selezionata.</p>
                      </div>
                    )}
                  </div>
                </GlassCardContent>
              </GlassCard>
            </>
          )}

          {!currentRun && !running && (
            <GlassCard>
              <GlassCardContent className="py-12">
                <div className="text-center text-muted-foreground dark:text-muted-foreground/70">
                  <FlaskConical className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p className="text-lg font-medium">Nessun test eseguito</p>
                  <p className="text-sm mt-1">Seleziona una categoria e un tenant, poi clicca "Avvia Test" per iniziare.</p>
                </div>
              </GlassCardContent>
            </GlassCard>
          )}
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <GlassCard>
            <GlassCardHeader>
              <div className="flex items-center justify-between">
                <GlassCardTitle className="flex items-center gap-2">
                  <History className="h-5 w-5" />
                  Storico Esecuzioni
                </GlassCardTitle>
                <Button variant="outline" size="sm" onClick={loadHistory}>
                  <RefreshCw className="h-4 w-4 mr-1" /> Aggiorna
                </Button>
              </div>
            </GlassCardHeader>
            <GlassCardContent>
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-purple-500" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground dark:text-muted-foreground/70">
                  <History className="h-12 w-12 mx-auto mb-3 opacity-20" />
                  <p>Nessuna esecuzione registrata</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border dark:border-white/10">
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-muted-foreground/70 uppercase">Data</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-muted-foreground/70 uppercase">Categoria</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-muted-foreground/70 uppercase">Tenant</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-muted-foreground/70 uppercase">Risultato</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground dark:text-muted-foreground/70 uppercase">Durata</th>
                      </tr>
                    </thead>
                    <tbody>
                      {history.map(run => (
                        <HistoryRow key={run.id} run={run} categories={categories} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </GlassCardContent>
          </GlassCard>
        </TabsContent>
      </Tabs>
    </div>
  );
}
